/**
 * The editor's Items card (design C): draggable lines with two rows of
 * fields, "+ Custom line" and "+ Add from catalogue".
 *
 * The catalogue reads the same sources as the Products and Services screens
 * (ProductRepository /products, ServiceRepository /categories). Courses have
 * no priced items in the dashboard yet, so that tab is disabled. Catalogue
 * prices are stored in rupees and converted to minor units by string
 * parsing (priceToMinor), never by float multiplication.
 */
import React, { useEffect, useRef, useState } from 'react';
import ProductRepository from '../../../internals/repository/ProductRepository';
import ServiceRepository from '../../../internals/repository/ServiceRepository';
import type { InvoiceSettings } from '../../../internals/repository/InvoiceRepository';
import { money, moneyInput } from '../format';
import { blankLine, EditorLine, lineField, LineKind, priceToMinor } from '../editorModel';
import { lineAmount, PreviewLine } from './InvoicePreview';

type Tab = 'PRODUCT' | 'SERVICE' | 'COURSE';
interface CatalogueItem { id: string; name: string; priceMinor: number; unit?: string }

const TABS: { key: Tab; label: string; disabled?: boolean }[] = [
  { key: 'PRODUCT', label: 'Products' },
  { key: 'SERVICE', label: 'Services' },
  { key: 'COURSE', label: 'Courses', disabled: true },
];

async function loadCatalogue(shopId: string, tab: Tab): Promise<CatalogueItem[]> {
  if (tab === 'PRODUCT') {
    const res: any = await ProductRepository.fetchProducts(shopId, null, 500, null);
    if (res?.error) throw res.error;
    return (res?.Items ?? []).map((p: any) => {
      const variants: any[] = Array.isArray(p.priceVariants) ? p.priceVariants : [];
      // The cheapest variant, as the Products list shows it.
      const prices = variants.map((v) => priceToMinor(v?.price)).filter((n) => n > 0);
      const priceMinor = prices.length ? Math.min(...prices) : priceToMinor(p.itemPrice);
      return { id: p.pid, name: String(p.productName ?? '').trim(), priceMinor };
    }).filter((p: CatalogueItem) => p.id && p.name).sort((a: CatalogueItem, b: CatalogueItem) => a.name.localeCompare(b.name));
  }
  if (tab === 'SERVICE') {
    const res: any = await ServiceRepository.fetchCategories(shopId);
    if (res?.error) throw res.error;
    const categories: any[] = (Array.isArray(res) ? res[0]?.categories : res?.categories) ?? [];
    return categories.flatMap((c) => (c.services ?? []).map((s: any) => ({
      id: s.id, name: String(s.serviceTitle ?? '').trim(), priceMinor: priceToMinor(s.price),
    }))).filter((s: CatalogueItem) => s.id && s.name).sort((a: CatalogueItem, b: CatalogueItem) => a.name.localeCompare(b.name));
  }
  return [];
}

function CatalogueMenu({ shopId, onPick, onClose }: { shopId?: string; onPick: (kind: LineKind, item: CatalogueItem) => void; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>('PRODUCT');
  const [q, setQ] = useState('');
  const [items, setItems] = useState<Partial<Record<Tab, CatalogueItem[]>>>({});
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!shopId || items[tab] || tab === 'COURSE') return;
    setError(null);
    loadCatalogue(shopId, tab)
      .then((list) => setItems((m) => ({ ...m, [tab]: list })))
      .catch(() => setError('The catalogue could not load.'));
  }, [shopId, tab, items]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [onClose]);

  const list = (items[tab] ?? []).filter((i) => !q.trim() || i.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div
      ref={ref}
      role="menu"
      aria-label="Add from catalogue"
      className="absolute right-0 top-[calc(100%+6px)] z-30 flex w-[340px] max-w-[calc(100vw-48px)] flex-col gap-1.5 rounded-xl border border-borderc bg-surface p-2 shadow-[var(--shadowLg)]"
    >
      <div className="flex gap-0.5 rounded-lg bg-surface2 p-[3px]" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            disabled={t.disabled}
            title={t.disabled ? 'Coming soon' : undefined}
            onClick={() => { setTab(t.key); setQ(''); }}
            className={`h-7 flex-1 rounded-md text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${
              tab === t.key ? 'bg-surface text-text1 shadow-sm' : 'text-text2'
            }`}
          >
            {t.label}{t.disabled ? ' · Soon' : ''}
          </button>
        ))}
      </div>
      <input
        className="nl-input nl-input--sm"
        aria-label="Search the catalogue"
        placeholder="Search"
        value={q}
        autoFocus
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="flex max-h-72 flex-col overflow-auto">
        {error && <span className="px-2.5 py-2 text-[13px] text-[color:var(--negFg)]">{error}</span>}
        {!error && !items[tab] && <span className="px-2.5 py-2 text-[13px] text-text3">Loading…</span>}
        {items[tab] && !list.length && <span className="px-2.5 py-2 text-[13px] text-text3">Nothing matches.</span>}
        {list.map((i) => (
          <button
            key={i.id}
            type="button"
            role="menuitem"
            onClick={() => onPick(tab, i)}
            className="flex justify-between gap-2.5 rounded-[7px] px-2.5 py-2 text-left text-[13px] text-text1 hover:bg-surface2 focus-visible:bg-surface2 focus-visible:outline-none"
          >
            <span className="font-medium">{i.name}</span>
            <span className="nl-num whitespace-nowrap text-text3">{money(i.priceMinor)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const smallLabel = 'flex flex-col gap-1 text-[11px] font-semibold text-text3';
const bad = (on: boolean) => (on ? '!border-neg' : '');

export default function LineItemsTable({
  shopId, lines, previewLines, settings, newLineTax, errors, cardError, onChange,
}: {
  shopId?: string;
  lines: EditorLine[];
  /** Same order as `lines`; figures come from the live preview. */
  previewLines: PreviewLine[];
  settings: InvoiceSettings | null;
  newLineTax: string;
  errors: Record<string, string>;
  cardError?: string;
  onChange: (lines: EditorLine[]) => void;
}) {
  const [menu, setMenu] = useState(false);
  const [drag, setDrag] = useState<string | null>(null);
  /** Only the ⋮⋮ handle starts a drag, so text in the inputs stays selectable. */
  const [armed, setArmed] = useState<string | null>(null);
  const rates = settings?.taxRates ?? [];

  const set = (key: string, patch: Partial<EditorLine>) =>
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const addCatalogue = (kind: LineKind, item: CatalogueItem) => {
    onChange([...lines, blankLine(newLineTax, {
      kind, refId: item.id, description: item.name, unitPrice: moneyInput(item.priceMinor), unit: item.unit ?? '',
    })]);
    setMenu(false);
  };

  const onDragOver = (e: React.DragEvent, overKey: string) => {
    e.preventDefault();
    if (!drag || drag === overKey) return;
    const from = lines.findIndex((l) => l.key === drag);
    const to = lines.findIndex((l) => l.key === overKey);
    if (from < 0 || to < 0) return;
    const next = lines.slice();
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };

  /** Keyboard reorder for the handle (drag is mouse only). */
  const move = (key: string, by: number) => {
    const from = lines.findIndex((l) => l.key === key);
    const to = from + by;
    if (to < 0 || to >= lines.length) return;
    const next = lines.slice();
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };

  return (
    <section
      aria-label="Items"
      className={`rounded-2xl border bg-surface ${cardError ? 'border-neg' : 'border-borderc'}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <h2 className="text-base font-semibold text-text1">Items</h2>
        <div className="relative flex gap-2">
          <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm" onClick={() => onChange([...lines, blankLine(newLineTax)])}>
            + Custom line
          </button>
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={menu}
            className="nl-btn nl-btn--soft nl-btn--sm border-brand"
            onClick={() => setMenu((m) => !m)}
          >
            + Add from catalogue
          </button>
          {menu && <CatalogueMenu shopId={shopId} onPick={addCatalogue} onClose={() => setMenu(false)} />}
        </div>
      </div>

      {lines.map((l, i) => {
        const err = (f: string) => errors[lineField(l.key, f)];
        const msgs = ['description', 'quantity', 'unitPrice', 'discount', 'taxCode', 'unit'].map(err).filter(Boolean);
        const pl = previewLines[i];
        // A tax code the settings no longer list stays selectable on old lines.
        const extraRate = l.taxCode && !rates.some((r) => r.code === l.taxCode) ? l.taxCode : null;
        return (
          <div
            key={l.key}
            draggable={armed === l.key}
            onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', l.key); } catch (x) { /* old browsers */ } setDrag(l.key); }}
            onDragOver={(e) => onDragOver(e, l.key)}
            onDrop={(e) => { e.preventDefault(); setDrag(null); }}
            onDragEnd={() => { setDrag(null); setArmed(null); }}
            className={`grid grid-cols-[18px_minmax(0,1fr)] gap-2.5 border-t border-borderc py-3 pl-3 pr-5 ${drag === l.key ? 'bg-brand-soft' : ''}`}
          >
            <button
              type="button"
              title="Drag to reorder"
              onMouseDown={() => setArmed(l.key)}
              onMouseUp={() => setArmed(null)}
              aria-label={`Reorder line ${i + 1}. Use the up and down arrow keys.`}
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp') { e.preventDefault(); move(l.key, -1); }
                if (e.key === 'ArrowDown') { e.preventDefault(); move(l.key, 1); }
              }}
              className="h-[38px] cursor-grab select-none rounded text-center text-[15px] leading-[38px] text-text3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
            >
              ⋮⋮
            </button>
            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex items-center gap-2.5">
                <input
                  className={`nl-input min-w-0 flex-1 font-medium ${bad(!!err('description'))}`}
                  style={{ height: 38 }}
                  aria-label={`Line ${i + 1} description`}
                  aria-invalid={!!err('description') || undefined}
                  placeholder="Item or description"
                  value={l.description}
                  onChange={(e) => set(l.key, { description: e.target.value })}
                />
                <span className="nl-num min-w-[110px] text-right font-semibold tabular-nums text-text1">
                  {money(pl ? lineAmount(pl) : 0)}
                </span>
                <button
                  type="button"
                  aria-label={`Remove line ${i + 1}`}
                  onClick={() => onChange(lines.filter((x) => x.key !== l.key))}
                  className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[7px] text-lg text-text3 hover:bg-surface2 hover:text-[color:var(--negFg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
                >
                  ×
                </button>
              </div>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(92px,1fr))] gap-2">
                <label className={smallLabel}>
                  Quantity
                  <input
                    className={`nl-input nl-input--sm tabular-nums ${bad(!!err('quantity'))}`}
                    inputMode="decimal"
                    aria-invalid={!!err('quantity') || undefined}
                    value={l.quantity}
                    onChange={(e) => set(l.key, { quantity: e.target.value })}
                  />
                </label>
                <label className={smallLabel}>
                  Unit
                  <input className={`nl-input nl-input--sm ${bad(!!err('unit'))}`} maxLength={20} placeholder="item" value={l.unit} onChange={(e) => set(l.key, { unit: e.target.value })} />
                </label>
                <label className={smallLabel}>
                  Unit price
                  <input
                    className={`nl-input nl-input--sm tabular-nums ${bad(!!err('unitPrice'))}`}
                    inputMode="decimal"
                    placeholder="0.00"
                    aria-invalid={!!err('unitPrice') || undefined}
                    value={l.unitPrice}
                    onChange={(e) => set(l.key, { unitPrice: e.target.value })}
                  />
                </label>
                <label className={smallLabel}>
                  Discount
                  <span className={`flex h-8 overflow-hidden rounded-lg border bg-surface focus-within:border-brand ${err('discount') ? 'border-neg' : 'border-borderc2'}`}>
                    <input
                      className="w-full min-w-0 bg-transparent px-2 text-[13px] font-normal tabular-nums text-text1 outline-none"
                      inputMode="decimal"
                      placeholder="0"
                      aria-invalid={!!err('discount') || undefined}
                      value={l.discount}
                      onChange={(e) => set(l.key, { discount: e.target.value })}
                    />
                    <select
                      aria-label="Discount type"
                      className="border-l border-borderc bg-surface2 px-1 text-xs font-normal text-text1 outline-none"
                      value={l.discountType}
                      onChange={(e) => set(l.key, { discountType: e.target.value as EditorLine['discountType'] })}
                    >
                      <option value="AMOUNT">Rs</option>
                      <option value="PERCENT">%</option>
                    </select>
                  </span>
                </label>
                <label className={smallLabel}>
                  Tax
                  <select
                    className={`nl-select nl-input--sm font-normal ${bad(!!err('taxCode'))}`}
                    value={l.taxCode}
                    onChange={(e) => set(l.key, { taxCode: e.target.value })}
                  >
                    {rates.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                    {extraRate && <option value={extraRate}>{extraRate}</option>}
                    <option value="">No tax</option>
                  </select>
                </label>
              </div>
              {msgs.length > 0 && (
                <span role="alert" className="text-xs font-semibold text-[color:var(--negFg)]">! {msgs[0]}</span>
              )}
            </div>
          </div>
        );
      })}

      {!lines.length && (
        <div className="flex flex-col items-center gap-1 border-t border-borderc px-5 py-8 text-center">
          <span className={`font-semibold ${cardError ? 'text-[color:var(--negFg)]' : 'text-text1'}`}>No items yet</span>
          <span className="text-[13px] text-text2">Add products, services or courses from the catalogue, or type a custom line.</span>
        </div>
      )}
    </section>
  );
}
